import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationOptions,
} from "@tanstack/react-query";

import { regroupVariantImages } from "@/lib/catalog-item-variants";
import {
  catalogCategoryTree,
  catalogItemAddVariant,
  catalogItemCreate,
  catalogItemDelete,
  catalogItemDetail,
  catalogItemsList,
  catalogItemUpdate,
} from "@/api/functions/catalog";
import type {
  AddCatalogItemVariantBrowserInput,
  CatalogItemByIdBrowserInput,
  ListCatalogItemsBrowserInput,
  RequestCatalogItemBrowserInput,
  UpdateCatalogItemBrowserInput,
} from "@/api/contracts/catalog";

// The Item Catalog list's data access: one page of items, keyed on the request
// that produced it.
//
// Two choices carry the page's behavior, and both are about what the user sees
// *between* two pages rather than on either one:
//
//   - **`keepPreviousData`.** A page flip is a new query key, and react-query's
//     default for a new key is `pending` with no data — which would blank the
//     list and collapse the layout on every click. `keepPreviousData` hands
//     back the previous page's rows instead, and raises `isPlaceholderData`
//     while it does; the page dims those rows under an overlay rather than
//     replacing them with a spinner. It is also what makes a *failed* refetch
//     survivable — but only a **same-key** one: the old rows stay on screen and
//     the error is a toast, not an empty page. A failure on a *new* key (a page
//     flip, a sort, a filter) has no placeholder to fall back on, because
//     react-query gates it on `status === 'pending'`, so the page shows its
//     error instead. That last half is a **divergence** from the donor and is
//     named on #461's decision round: RTK Query's `data` is "the latest result
//     regardless of hook arg" and its `lastResult` chain survives an arg change
//     (`rtk-query-react.legacy-esm.js:180`), so the donor renders the previous
//     page's rows, fully legible, with no error shown anywhere (`isError` is
//     destructured unused at `items-list-view.tsx:71`; the page-level branch is
//     `isError && !rawData`, `items-list-page.tsx:511`). Pre-existing here, and
//     shared with the card and list views.
//   - **A 30-second `staleTime`.** The catalog is edited rarely and browsed
//     constantly, so paging back and forth should not refetch. It is short
//     enough that an edit made in another tab shows up on the next visit, and
//     long enough that walking a result set costs one request per new page.
//
// The key carries the whole input rather than a hand-picked subset. Every field
// on it changes which rows come back, so a key that dropped one would serve a
// cached page from a different request — which is also why the filters needed
// no edit here when they arrived.

/**
 * What every item-list key starts with, and what a write invalidates.
 *
 * Exported as its own value because the create mutation below and the batch
 * writes both have to name it: a second spelling of `["catalog", "items"]`
 * would keep passing while quietly ceasing to match the keys it is meant to
 * invalidate.
 */
export const catalogItemsQueryKeyPrefix = ["catalog", "items"] as const;

export const catalogItemsQueryKey = (input: ListCatalogItemsBrowserInput) =>
  [...catalogItemsQueryKeyPrefix, input] as const;

export const CATALOG_ITEMS_STALE_TIME_MS = 30_000;

export const catalogItemsQueryOptions = (input: ListCatalogItemsBrowserInput) =>
  queryOptions({
    queryKey: catalogItemsQueryKey(input),
    queryFn: () => catalogItemsList({ data: input }),
    placeholderData: keepPreviousData,
    staleTime: CATALOG_ITEMS_STALE_TIME_MS,
  });

export function useCatalogItems(input: ListCatalogItemsBrowserInput) {
  return useQuery(catalogItemsQueryOptions(input));
}

// The category tree behind the filter sidebar's category select.
//
// Its own query rather than a field on the list's: the tree changes when an
// admin edits a category, not when the user pages through items, so binding it
// to the list's key would re-read it on every page flip and throw it away on
// every filter change.
//
// A **long** `staleTime` for the same reason — the tree is small, read on every
// visit to the list, and changes only when an admin edits the category tree.
// Ten minutes is short enough that such an edit shows up within a sitting, and
// long enough that walking the catalog never re-reads it. There is no category
// *write* on the BFF yet (the category-admin surfaces are their own ticket);
// the one that lands owes this key an invalidation, exactly as each lookup
// write invalidates its own kind's key in `@/hooks/catalog-admin`.

export const catalogCategoryTreeQueryKey = ["catalog", "categories", "tree"] as const;

export const CATALOG_CATEGORY_TREE_STALE_TIME_MS = 600_000;

export const catalogCategoryTreeQueryOptions = () =>
  queryOptions({
    queryKey: catalogCategoryTreeQueryKey,
    queryFn: () => catalogCategoryTree(),
    staleTime: CATALOG_CATEGORY_TREE_STALE_TIME_MS,
  });

export function useCatalogCategoryTree() {
  return useQuery(catalogCategoryTreeQueryOptions());
}

// ---------------------------------------------------------------------------
// Requesting an item
// ---------------------------------------------------------------------------

/**
 * The Request Item dialog's submit.
 *
 * **Invalidates the whole item-list key on success, and only on success.** The
 * new item is in Requested status, so it shows in the list under the filters
 * that admit an unapproved item — the donor does the same thing by emitting an
 * event the search grid listens for. Every page of every filter combination is
 * a separate key, so the prefix is what is invalidated: the one page on screen
 * refetches and the rest are marked stale for whenever they are next read.
 *
 * A refused create changed nothing on the server, and re-reading after one would
 * replace the list the typist is looking at for no reason.
 *
 * Takes the client explicitly so the invalidation is testable without rendering
 * a provider — the shape `createLookupMutationOptions` established.
 */
export function requestCatalogItemMutationOptions(
  queryClient: QueryClient,
): UseMutationOptions<
  Awaited<ReturnType<typeof catalogItemCreate>>,
  Error,
  RequestCatalogItemBrowserInput
> {
  return {
    mutationFn: (data) => catalogItemCreate({ data }),
    onSuccess: () => {
      // Started, not awaited. react-query settles the mutation only after a
      // promise returned from here resolves, so returning the invalidation would
      // hold the dialog's success state behind a refetch of a list the typist
      // has stopped looking at. The rows catch up when they catch up.
      void queryClient.invalidateQueries({ queryKey: catalogItemsQueryKeyPrefix });
    },
  };
}

export function useRequestCatalogItem() {
  const queryClient = useQueryClient();
  return useMutation(requestCatalogItemMutationOptions(queryClient));
}

// ---------------------------------------------------------------------------
// One item: the details page's read, and its delete
// ---------------------------------------------------------------------------

/**
 * One item's detail, keyed **under the list's own prefix**.
 *
 * `["catalog", "items", "detail", 7]` rather than a sibling `["catalog",
 * "item", 7]`, and that is the whole reason the delete below invalidates one
 * key instead of two: a retired item has to leave every list page *and* stop
 * showing as live on the page it was deleted from, and one prefix covers both.
 * A sibling key would work until someone added the second write and forgot the
 * second invalidation.
 *
 * The literal `"detail"` segment is what keeps it from colliding with a list
 * key, whose third element is the request object.
 */
export const catalogItemDetailQueryKeyPrefix = [...catalogItemsQueryKeyPrefix, "detail"] as const;

export const catalogItemDetailQueryKey = (itemId: number) =>
  [...catalogItemDetailQueryKeyPrefix, itemId] as const;

export const catalogItemDetailQueryOptions = (itemId: number) =>
  queryOptions({
    queryKey: catalogItemDetailQueryKey(itemId),
    queryFn: () => catalogItemDetail({ data: { itemId } }),
    // The same 30 seconds the list carries, for the same reason: a user walking
    // back and forth between the list and an item should not re-read the item
    // on every visit, and an edit made elsewhere still shows up within a sitting.
    staleTime: CATALOG_ITEMS_STALE_TIME_MS,
  });

/**
 * One item's details.
 *
 * `enabled` is react-query's equivalent of the donor's RTK Query `skip`, and the
 * card's variant preview is what needs it: `useGetItemQuery({ itemId }, { skip:
 * isOwnItem })` (`item-card.tsx:79-82`) means a card that is showing its own item
 * — every card, until someone clicks a tile — costs no request at all.
 */
export function useCatalogItemDetail(itemId: number, options?: { enabled?: boolean }) {
  return useQuery({ ...catalogItemDetailQueryOptions(itemId), enabled: options?.enabled });
}

// ---------------------------------------------------------------------------
// Adding a variant
// ---------------------------------------------------------------------------

/**
 * Correct every cached detail of the group a write just changed.
 *
 * A create-variant and a delete both change *membership*, and membership is a
 * fact about the whole group: each member's cached detail carries its own
 * `variants` list, and all of them go stale together. The response's `variants`
 * is the correction for all of them — one write, one patch pass, rather than
 * each member waiting for a refetch to notice.
 *
 * `itemId` is the item the write named, which is not necessarily the response's
 * item and not necessarily any given cache entry's item; an entry is in the
 * group when it *is* that item or lists it among its variants. An entry holding
 * nothing is skipped, so a member nobody has read needs no guard.
 *
 * `variantImages` cannot transfer as-is the way `variants` can — it is siblings
 * *of the response's item*, and each entry needs siblings of its own. See
 * `regroupVariantImages`; without it a new chip appears before its pictures do.
 *
 * Both call sites patch identically, so the shape lives here once: a response
 * that grows another group-shaped field should have one place to learn about
 * it, not two that drift.
 */
function correctCachedGroups(
  queryClient: QueryClient,
  itemId: number,
  result: NonNullable<Awaited<ReturnType<typeof catalogItemDetail>>>,
): void {
  queryClient.setQueriesData(
    { queryKey: catalogItemDetailQueryKeyPrefix },
    (cached: Awaited<ReturnType<typeof catalogItemDetail>> | undefined) => {
      if (cached == null) return cached;
      const inGroup = cached.item.id === itemId || cached.variants.some(({ id }) => id === itemId);
      return inGroup
        ? {
            ...cached,
            variants: result.variants,
            variantImages: regroupVariantImages(cached.item.id, result),
          }
        : cached;
    },
  );
}

/**
 * Add one variant of an item — the Add Variant dialog's submit.
 *
 * Two different things have to happen on success, and only one of them is an
 * invalidation:
 *
 *   - **The parent's cached group gains the new member, immediately.** The
 *     response is the *new variant's* detail, and a detail response's `variants`
 *     is the whole group — so the parent's cached read can be corrected from it
 *     without a round trip. That is what lets the details page switch onto the
 *     variant on the same frame the dialog closes, which is what the donor does:
 *     its mutation response lands in the object cache and the swap is a cache
 *     read (`useVariantForm.ts`, then `useAddVariantSuccessObserver`). Without
 *     it the page would show the parent until a refetch landed and then jump.
 *   - **Every list page is invalidated**, because a new item belongs in the
 *     list under the filters that admit it, and its siblings' tile strips have
 *     gained a tile. The prefix covers the parent's detail read too, so the
 *     optimistic write above is confirmed against the server rather than
 *     trusted — it is the same data, so nothing moves when it lands.
 *
 * No `refetchType` exception here, unlike the delete: nothing is being navigated
 * away from and every re-read answers something that still exists.
 *
 * Takes the client explicitly so both halves are testable without rendering a
 * provider.
 */
export function addCatalogItemVariantMutationOptions(
  queryClient: QueryClient,
): UseMutationOptions<
  Awaited<ReturnType<typeof catalogItemAddVariant>>,
  Error,
  AddCatalogItemVariantBrowserInput
> {
  return {
    mutationFn: (data) => catalogItemAddVariant({ data }),
    onSuccess: (result, { itemId }) => {
      // **Every cached detail of a group member, not just the parent's own.**
      //
      // The key the details page reads under is the id in its *URL*, and the
      // parent a variant is added to is the id of the variant on *screen* — the
      // donor's action menu reads `item.id`, so those two differ the moment a
      // chip has been clicked. Patching only `itemId` would then leave the
      // page's own cached entry without the new member, `displayedVariant`
      // would fall through to the item the URL named, and the page would snap
      // back to it until a refetch landed.
      correctCachedGroups(queryClient, itemId, result);
      // Started, not awaited: the dialog closes on success, and its button
      // would otherwise stay in its loading state until a refetch of a list
      // nobody is looking at came back.
      void queryClient.invalidateQueries({ queryKey: catalogItemsQueryKeyPrefix });
    },
  };
}

export function useAddCatalogItemVariant() {
  const queryClient = useQueryClient();
  return useMutation(addCatalogItemVariantMutationOptions(queryClient));
}

/**
 * Retire one item — the details page's Delete.
 *
 * Soft on the Python side, and **refused there** while any specification uses
 * the item (`delete-refused:item`, a tagged error the scrub allowlist passes
 * through intact). The menu's disabled state is the affordance for that; this
 * is the call, and it must not assume the affordance held.
 *
 * Invalidates on success only, like the create above and for the same reason: a
 * refused delete changed nothing on the server, and re-reading after one would
 * replace the page the user is looking at to show them exactly what is already
 * there.
 *
 * Takes the client explicitly so the invalidation is testable without rendering
 * a provider.
 */
/** Whether a cached key is the one item's detail read — the delete's exception. */
function isDetailKeyFor(queryKey: readonly unknown[], itemId: number): boolean {
  const detail = catalogItemDetailQueryKey(itemId);
  return queryKey.length === detail.length && detail.every((part, i) => queryKey[i] === part);
}

export function deleteCatalogItemMutationOptions(
  queryClient: QueryClient,
): UseMutationOptions<
  Awaited<ReturnType<typeof catalogItemDelete>>,
  Error,
  CatalogItemByIdBrowserInput
> {
  return {
    mutationFn: (data) => catalogItemDelete({ data }),
    onSuccess: (result, { itemId }) => {
      // **The surviving group, straight off the response.** A delete answers
      // with the retired item's own detail, and a detail's `variants` is
      // `list_variants` — "alive items sharing `variant_group_id`", which
      // filters `deleted_at IS NULL` (`catalog_item_repository.py:326-338`, and
      // `test_variants_are_the_alive_items_sharing_a_group_id` pins it). So the
      // response already carries the group *without* the item just deleted.
      //
      // Patching from it matters because a sibling delete leaves the page up:
      // the route falls back to the URL's item and stays (`deletedItemWasVariant`),
      // so a stale chip for the deleted sibling would sit there clickable until
      // the invalidation below came back — and indefinitely if that read failed.
      // Clicking it would then render an item that no longer exists.
      correctCachedGroups(queryClient, itemId, result);
      // The deleted item's own read is marked stale and **deliberately not
      // refetched**, which is what `refetchType: "none"` buys: the details page
      // is still mounted for the moment it takes to navigate away, so any
      // re-read here — an invalidation, or dropping the entry, which makes the
      // live observer fetch a replacement — would read the item that was just
      // deleted, get the 404 page's `null` back, and yank the page out from
      // under the user on their way out. Stale-not-refetched still means a
      // later visit to that URL re-reads rather than showing a deleted item as
      // live.
      void queryClient.invalidateQueries({
        queryKey: catalogItemDetailQueryKey(itemId),
        refetchType: "none",
      });
      // Started, not awaited — the page navigates away on success, and holding
      // that behind a refetch of a list nobody is looking at would leave the
      // confirmation dialog spinning over an item that is already gone.
      //
      // The prefix covers the detail key too, hence the predicate: without it
      // this call would undo the line above and refetch the deleted item.
      void queryClient.invalidateQueries({
        queryKey: catalogItemsQueryKeyPrefix,
        predicate: (query) => !isDetailKeyFor(query.queryKey, itemId),
      });
    },
  };
}

export function useDeleteCatalogItem() {
  const queryClient = useQueryClient();
  return useMutation(deleteCatalogItemMutationOptions(queryClient));
}

/**
 * The item edit page's Save — the changed fields, and nothing else.
 *
 * Two things happen on success, and the split between them is the point:
 *
 *   - **The response is written straight into the details key.** The save
 *     answers with the item it just wrote, so the page it is on keeps rendering
 *     without a re-read — and the form re-seeds its baseline from it, which is
 *     what makes a *second* save diff against what was actually stored. The
 *     donor never re-seeds (`useEditProduct.ts` keeps its original
 *     `formDefaultData`), so its second save diffs against a moving baseline and
 *     re-sends fields that are already saved. Writing the row here is what
 *     closes that.
 *   - **Every list key is invalidated, the detail key excluded.** An edit can
 *     move an item in or out of any page's filters — status, Core and category
 *     are all editable on this screen — so the lists go stale. The detail key is
 *     excluded because the line above just put the authoritative answer in it;
 *     invalidating it would throw that away to re-read a row the response
 *     already carries. That exclusion is the same predicate the delete uses, and
 *     for the same structural reason: the detail key lives *under* the list
 *     prefix.
 *
 * On failure, nothing. A refused save changed nothing on the server, and the
 * editor is still looking at their draft — re-reading would be the one thing
 * that could take it away from them.
 *
 * Takes the client explicitly so both halves are testable without a provider.
 */
export function updateCatalogItemMutationOptions(
  queryClient: QueryClient,
): UseMutationOptions<
  Awaited<ReturnType<typeof catalogItemUpdate>>,
  Error,
  UpdateCatalogItemBrowserInput
> {
  return {
    mutationFn: (data) => catalogItemUpdate({ data }),
    // **The detail read is stood down before the write goes out.**
    // `setQueryData` below only puts the saved row in the cache; it does not
    // stop a GET already on the wire, and a read that started before the PATCH
    // resolves after it — overwriting the saved row with the one it found
    // before the save, and leaving the form's baseline describing a version
    // the server no longer holds. Cancelling first is React Query's own answer
    // to that race, and it is a no-op when nothing is in flight.
    onMutate: async ({ itemId }) => {
      await queryClient.cancelQueries({ queryKey: catalogItemDetailQueryKey(itemId) });
    },
    onSuccess: (result, { itemId }) => {
      queryClient.setQueryData(catalogItemDetailQueryKey(itemId), result);
      // **An edit can replace the gallery**, so the group's other members are
      // stale in exactly the way #413 forbids: their cached `variantImages`
      // still holds the pictures this save just deleted, and a chip clicked
      // from one of their pages would draw them. The invalidation below does
      // reach those keys, but it is a round trip — the deleted pictures would
      // be on screen until it landed, and stay there if it failed. Correcting
      // from the response is immediate and cannot fail.
      //
      // Ordered after the write above, and a no-op for the edited item's own
      // entry: `regroupVariantImages` seen from the response's own item is the
      // response's own `variantImages`.
      correctCachedGroups(queryClient, itemId, result);
      // Started, not awaited: the page stays put and raises its snackbar, and
      // holding the mutation's settle behind a refetch of a list nobody is
      // looking at would leave the Save button spinning after the save landed.
      void queryClient.invalidateQueries({
        queryKey: catalogItemsQueryKeyPrefix,
        predicate: (query) => !isDetailKeyFor(query.queryKey, itemId),
      });
    },
  };
}

export function useUpdateCatalogItem() {
  const queryClient = useQueryClient();
  return useMutation(updateCatalogItemMutationOptions(queryClient));
}
